import React, { useEffect, useState, useMemo, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import TournamentMatchesTable from "Components/Tournament/TournamentMatchesTable";
import StatsTable from "Components/Tournament/StatsTable";
import CreateMatchesButton from "Components/Tournament/CreateMatchesButton";
import DeleteMatchesButton from "Components/Tournament/DeleteMatchesButton";
import TeamsTables from "Components/Tournament/TeamsTables";
import { canManage } from "services/tournamentService";
import { useAuth } from "context/AuthProvider";
import { getMatchesByTournamentId } from "services/matchService";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import EliminationPhaseManager from "Components/Tournament/EliminationPhaseManager";
import EliminationMatchesTable from "Components/Tournament/EliminationMatchesTable";
import EliminationBracket from "Components/Tournament/EliminationBracket";
import CompileTournament from "Components/Tournament/CompileTournament";

const TournamentProgressPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { tournamentId } = useParams();
  const [tournament, setTournament] = useState(null);
  const [tournamentLoading, setTournamentLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const navigate = useNavigate();
  const [scrollYBeforeRefresh, setScrollYBeforeRefresh] = useState(null);

  const reloadMatches = () => {
    setScrollYBeforeRefresh(window.scrollY); // Save scroll before refreshing
    setRefreshKey((prev) => prev + 1);
  };

  const isAdmin = useMemo(() => {
    return user && tournament ? canManage(user, tournament.locationId) : false;
  }, [user, tournament]);

  useLayoutEffect(() => {
    if (!matchesLoading && scrollYBeforeRefresh !== null) {
      window.scrollTo({ top: scrollYBeforeRefresh, behavior: "auto" });
      setScrollYBeforeRefresh(null); // Reset after scrolling
    }
  }, [matchesLoading, scrollYBeforeRefresh]);

  useEffect(() => {
    const fetchTournament = async () => {
      try {
        const snapshot = await getDoc(doc(db, "tournaments", tournamentId));
        if (snapshot.exists()) {
          setTournament({ id: snapshot.id, ...snapshot.data() });
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error("Erreur lors du chargement du tournoi :", error);
      } finally {
        setTournamentLoading(false);
      }
    };

    fetchTournament();
  }, [tournamentId]);

  useEffect(() => {
    const fetchMatches = async () => {
      if (!tournament?.id) return;
      setMatchesLoading(true);
      try {
        const fetchedMatches = await getMatchesByTournamentId(tournament.id);
        setMatches(fetchedMatches);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Erreur lors du chargement des matchs :", error);
      } finally {
        setMatchesLoading(false);
      }
    };

    fetchMatches();
  }, [tournament?.id, refreshKey]);

  if (authLoading || tournamentLoading || matchesLoading) {
    return <p className="text-center text-gray-500">Chargement...</p>;
  }

  if (!tournament) {
    return <p className="text-center text-red-500">Tournoi introuvable.</p>;
  }

  return (
    <div className="w-full mx-auto space-y-8 flex flex-col items-center">
      <div className="mx-auto w-3/5">
        <button
          onClick={() => navigate(-1)}
          className="text-lg font-bold bg-gray-700 ml-4 hover:bg-gray-600 shadow-xl text-white px-4 py-2 rounded-md transition mt-8"
        >
          ⬅️ Retour
        </button>
      </div>
      <div className="flex flex-col items-center w-full md:w-2/3 mb-4">
        <TeamsTables teams={tournament.teams} />
      </div>

      {isAdmin && (
        <>
          <div className="flex flex-wrap gap-2">
            {!matches.length > 0 && (
              <CreateMatchesButton
                tournament={tournament}
                onCreated={reloadMatches}
              />
            )}
            {matches.length > 0 && !tournament.compiled && (
              <DeleteMatchesButton
                tournament={tournament}
                onDeleted={reloadMatches}
              />
            )}
          </div>

          <h2 className="text-2xl font-bold">
            Matchs – {tournament.name || "Tournoi"}
          </h2>

          <TournamentMatchesTable
            matches={matches}
            setMatches={setMatches}
            tournament={tournament}
          />
          <StatsTable matches={matches} teams={tournament.teams} />
          <div className="w-full flex flex-col items-center bg-gray-600 p-4 gap-8">
            <EliminationPhaseManager
              tournament={tournament}
              matches={matches}
              reloadMatches={reloadMatches}
            />
            <EliminationMatchesTable
              matches={matches}
              setMatches={setMatches}
              tournament={tournament}
              reloadMatches={reloadMatches}
            />
            <EliminationBracket matches={matches} />
          </div>
          {isAdmin && (
            <div className="flex flex-col items-center p-4 w-full">
              <CompileTournament tournament={tournament} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TournamentProgressPage;
